/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ImpressionProvider, useImpression } from '@thd-olt-component-react/impression';
import {
  arrayOf, bool, params, shape, string, useDataModel
} from '@thd-nucleus/data-sources';
import { useStore, useConfigService } from '@thd-nucleus/experience-context';
import classNames from 'classnames';
import { PRODUCT_POD_V7_CLICK } from './constants';

const DEFAULT_ANALYTICS_DATA = { position: 0 };
const COMPONENT_ACTION_MAP = {
  ':Price:': 'price',
  ':FulfillmentPodStore:': 'fulfillment store',
  ':FulfillmentPodShipping:': 'fulfillment shipping',
  ':ProductSponsored:': 'sponsored label'
};
const DEFAULT_ANALYTIC_IMPRESSION_DATA = {
  id: null,
  name: null,
  component: null
};

const SKIP_POD_CLICK_SELECTORS = [
  'input',
  'button', 
  'select',
  'textarea',
  '[data-component*=":ProductHeader:"]',
  '[data-component*=":SuperSkuMiniSwatches:"]',
  '[data-component*=":Price:"]',
  '[data-component*=":CheckNearByStore:"]',
  '[data-component*=":checkAvailabilityButton:"]',
  '[data-testid="product-compare-label"]',
  '[data-testid="view-similar-pill"]'
].join(', ');

const shouldSkipPodClick = (event) => {
  if (event.nativeEvent?._podAnalyticsHandled) {
    return true;
  }

  const { target } = event;
  if (!(target instanceof Element)) {
    return true;
  }

  return !!target.closest(SKIP_POD_CLICK_SELECTORS);
};

export const ProductPod = ({
  render,
  itemId,
  storeId: storeIdFromProp = null,
  analyticsData = DEFAULT_ANALYTICS_DATA,
  hoverDelay = 500,
  onHover = null,
  padding = null,
  gap = null,
  borderRadius = null,
  analyticImpressionData = DEFAULT_ANALYTIC_IMPRESSION_DATA,
  hasSSKU = false,
  enableDefaultClickHandler = false,
}) => {
  const [selectedId, setSelecetedId] = useState(itemId);
  const [hoveredId, setHoveredId] = useState(undefined);
  const [productQuantity, setProductQuantity] = useState(1);
  const [loadedItemIds, setLoadedItemIds] = useState([]);
  const analyticsMigrationEnabled = useConfigService('THD_ANALYTICS_MIGRATION_ENABLED');
  const [sponsoredValues, setSponsoredValues] = useState({
    isSponsored: false,
    sponsoredBeacon: null,
    sponsoredMetadata: null
  });
  const [podState, setPodState] = useState({
    showSecondaryImage: false
  });

  const store = useStore();
  const storeId = storeIdFromProp || store?.storeId;
  const activeItemId = hoveredId || selectedId || itemId || null;

  const { data } = useDataModel('product', {
    variables: {
      itemId: activeItemId,
      storeId
    },
    skip: !activeItemId
  });

  const { product } = data || {};
  const {
    isSponsored,
    sponsoredBeacon,
    sponsoredMetadata,
    productSubType,
    swatches
  } = product?.info || {};
  const parentId = product?.identifiers?.parentId || null;
  const canonicalUrl = product?.identifiers?.canonicalUrl || null;

  useEffect(() => {
    if (product?.itemId && Object.keys(analyticsData).length) {
      const loadedState = loadedItemIds.find((obj) => obj.itemId === product.itemId);
      const isLoaded = !!loadedState;
      if (!isLoaded || (isLoaded && !loadedState?.isSponsored && isSponsored)) {
        const newSponsoredValues = {
          isSponsored: !!(sponsoredValues?.isSponsored || isSponsored),
          sponsoredBeacon: sponsoredValues?.sponsoredBeacon || sponsoredBeacon,
          sponsoredMetadata: sponsoredValues?.sponsoredMetadata || sponsoredMetadata
        };

        const position = analyticsData?.position + 1;
        const productPodLoadObj = {
          ...analyticsData,
          position,
          sponsoredValues: newSponsoredValues,
          sponsoredProduct: isSponsored,
          itemId: product?.itemId,
          badges: product?.badges
        };
        // NOTE: does this need to be updated to v8 ???
        if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
          if (analyticsMigrationEnabled) {
          // use lifecycle trigger method
            LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('product-pod-v7.load', productPodLoadObj);
          } else {
            LIFE_CYCLE_EVENT_BUS.trigger('product-pod-v7.load', productPodLoadObj);
          }
        }

        if (isLoaded) {
          setLoadedItemIds(loadedItemIds.map((obj) => {
            if (obj.itemId === product.itemId) {
              return { itemId: product.itemId, isSponsored: newSponsoredValues.isSponsored };
            }
            return obj;
          }));
        } else {
          setLoadedItemIds([...loadedItemIds, {
            itemId: product.itemId, isSponsored: newSponsoredValues.isSponsored
          }]);
        }
        setSponsoredValues(newSponsoredValues);
      }
    }
  }, [analyticsData, product?.itemId, isSponsored]);

  let useImpressionData = {
    id: itemId,
    name: 'ProductPod',
    component: 'ProductPod',
    position: analyticsData?.position + 1,
    type: isSponsored ? 'sponsoredproduct' : 'product',
    // eslint-disable-next-line no-nested-ternary
    category: swatches?.length > 1 ? hasSSKU ? 'ssku' : '' : '',
    // eslint-disable-next-line no-nested-ternary
    anchorSku: swatches?.length > 1 ? hasSSKU ? parentId : '' : ''
  };

  if (sponsoredMetadata) {
    useImpressionData = {
      ...useImpressionData,
      ...sponsoredMetadata,
    };
  }

  const { onViewBeacon, onViewBeacons } = sponsoredBeacon || {};

  if (onViewBeacon) {
    useImpressionData.sponsoredViewBeacon = onViewBeacon;
  }

  if (!!onViewBeacons && onViewBeacons.length > 0) {
    useImpressionData.sponsoredViewBeacons = onViewBeacons;
  }

  const impressionContextData = useImpression({ data: useImpressionData });
  const productPodRef = impressionContextData.ref;

  const idToDisplay = hoveredId || selectedId;

  const preFetchTimerRef = useRef(null);
  const onProductPodHover = (hoveredItemId) => {
    if (onHover) {
      preFetchTimerRef.current = setTimeout(() => {
        onHover(hoveredItemId);
      }, hoverDelay);
    }
  };

  const onProductPodHoverExit = () => {
    clearInterval(preFetchTimerRef.current);
  };

  // Root-level click delegation handler for pod analytics.
  // Sub-components (ProductImage, ProductRating, ProductATC) fire their own
  // PRODUCT_POD_V7_CLICK event and set event.nativeEvent._podAnalyticsHandled = true to
  // prevent this handler from firing a duplicate event as the click bubbles up.
  // Only attached to the root div when enableDefaultClickHandler is true — consumers that
  // don't opt in get no handler at all, preserving exact pre-existing behavior.
  const onProductPodClick = (event) => {
    if (shouldSkipPodClick(event)) {
      return;
    }

    const { target } = event;

    // Deferred to next event loop tick so Adobe Launch's eventProcessing handler
    // runs after the consuming app's synchronous onClick has populated digitalData.product.
    // A synchronous trigger here causes: TypeError: Cannot read properties of undefined (reading 'ID').
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
      // Walk up the DOM from the click target to find a known component boundary
      // identified by data-component attributes set by Harmony sub-components.
      // Falls back to 'product pod' if no mapped component is found (e.g. badge, border).
      const matchedComponent = Object.keys(COMPONENT_ACTION_MAP)
        .find((key) => target.closest(`[data-component*="${key}"]`));

      const productPodEventData = {
        podAction: matchedComponent ? COMPONENT_ACTION_MAP[matchedComponent] : 'product pod',
        podAnchorSku: activeItemId,
        target: '_self',
        parent: analyticsData?.parent || 'plp'
      };

      setTimeout(() => {
        window.LIFE_CYCLE_EVENT_BUS.trigger(PRODUCT_POD_V7_CLICK, productPodEventData);
      }, 0);
    }

    // Navigate to the product page. Skip if the click was on an anchor — the browser
    // handles navigation naturally. ATC/form-control clicks are excluded above.
    if (canonicalUrl && !target.closest('a[href]')) {
      window.location.href = canonicalUrl;
    }
  };

  const onChange = (clickData) => {
    if (clickData.itemId) {
      setSelecetedId(clickData.itemId);
    }
  };

  const onSSKUHover = (hoverData) => {
    const { itemId: hoveredItemId, isHovered } = hoverData;
    if (isHovered && hoveredItemId) {
      setHoveredId(hoveredItemId);
    } else {
      setHoveredId(undefined);
    }
  };

  const onSavingsCenterToggle = () => {
    setPodState({
      ...podState,
      showSecondaryImage: !podState.showSecondaryImage
    });
  };

  const onQuantityUpdate = ({ quantity }) => {
    setProductQuantity(quantity);
  };

  const state = {
    onChange,
    onSSKUHover,
    onSavingsCenterToggle,
    itemId: idToDisplay,
    storeId,
    quantity: productQuantity,
    onQuantityUpdate,
    ref: productPodRef,
    productSubType: productSubType?.name,
    ...podState
  };

  const impressionData = {
    id: analyticImpressionData?.id || itemId,
    name: analyticImpressionData?.name || 'ProductPod',
    component: analyticImpressionData?.component || 'ProductPod',
    type: isSponsored ? 'sponsoredproduct' : 'product',
  };

  const productPodParentClasses = classNames(
    padding, gap, borderRadius,
    'sui-flex sui-flex-col sui-relative',
    'sui-h-full sui-w-full sui-mb-2 sui-bg-primary',
    'sui-w-full sui-mb-2 sui-bg-primary', {
      'sui-p-4': !padding,
      'sui-gap-2': !gap
    });

  return (
    <div
      ref={productPodRef}
      data-testid="product-pod"
      className={productPodParentClasses}
      onMouseEnter={() => onProductPodHover(idToDisplay)}
      onMouseLeave={() => onProductPodHoverExit()}
      onClick={enableDefaultClickHandler ? onProductPodClick : undefined}
      /* eslint-disable-next-line react/no-unknown-property */
      clickid={impressionContextData?.clickID}
      data-component="ProductPod"
    >
      <ImpressionProvider data={impressionData} additionalData={analyticsData}>
        {render(state)}
      </ImpressionProvider>
    </div>
  );

};

ProductPod.displayName = 'ProductPod';

ProductPod.propTypes = {
  render: PropTypes.func.isRequired,
  itemId: PropTypes.string.isRequired,
  storeId: PropTypes.string,
  hoverDelay: PropTypes.number,
  onHover: PropTypes.func,
  padding: PropTypes.string,
  gap: PropTypes.string,
  borderRadius: PropTypes.string,
  hasSSKU: PropTypes.bool,
  enableDefaultClickHandler: PropTypes.bool,
  analyticsData: PropTypes.shape({
    position: PropTypes.number,
    parent: PropTypes.string.isRequired
  }),
  analyticImpressionData: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    component: PropTypes.string
  })
};

ProductPod.dataModel = {
  product: params({
    itemId: string().isRequired(),
    dataSource: string()
  }).shape({
    itemId: string(),
    dataSources: string(),
    badges: params({ storeId: string() }).arrayOf(shape({
      name: string(),
    })),
    info: shape({
      isSponsored: bool(),
      sponsoredMetadata: shape({
        campaignId: string(),
        placementId: string(),
        slotId: string(),
        sponsoredId: string(),
        trackSource: string()
      }),
      sponsoredBeacon: shape({
        onClickBeacon: string(),
        onViewBeacon: string(),
        onClickBeacons: arrayOf(string()),
        onViewBeacons: arrayOf(string())
      }),
      productSubType: shape({
        name: string()
      }),
    }),
    identifiers: shape({
      parentId: string(),
      canonicalUrl: string()
    }),
  }),
};
